import { useMemo, useState } from 'react'
import { n } from '../format'

/**
 * Chooses what to break down. Defaults to the low-cardinality columns — the
 * status/code style columns are exactly the ones worth counting, and the sampled
 * stats already tell us which those are.
 */
export function suggestInsightColumns(columns) {
  const categorical = columns.filter((c) => c.distinct && c.distinct.length > 1 && c.distinct.length <= 30)
  return (categorical.length > 0 ? categorical : columns).slice(0, 3).map((c) => c.name)
}

export function InsightsPanel({ columns, draft, setDraft, combine, setCombine, applied, onRun, onExit, scanning, scopeLabel }) {
  const [search, setSearch] = useState('')
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? columns.filter((c) => c.name.toLowerCase().includes(q)) : columns
  }, [columns, search])

  const chosen = [...draft]
  const active = applied !== null
  const unchanged = active && JSON.stringify(applied.columns) === JSON.stringify(chosen) && applied.combine === combine

  const toggle = (name, on) => {
    const next = new Set(draft)
    if (on) next.add(name)
    else next.delete(name)
    setDraft(next)
  }

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Insights</h2>
        {active && <span className="badge on">on</span>}
      </header>

      <p className="hint">
        {active
          ? `Counting ${scopeLabel} by ${applied.columns.join(', ')}.`
          : `Row counts per value, over ${scopeLabel}.`}
      </p>

      {columns.length > 10 && (
        <input
          className="input tiny"
          placeholder="Find column…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="value-options dup-columns">
        {visible.map((c) => (
          <label key={c.name} className="value-option">
            <input type="checkbox" checked={draft.has(c.name)} onChange={(e) => toggle(c.name, e.target.checked)} />
            <span className="value-text" title={c.name}>
              {c.name}
            </span>
            {c.distinct && <span className="value-count">{n(c.distinct.length)}</span>}
          </label>
        ))}
        {visible.length === 0 && <span className="muted">No matching column.</span>}
      </div>

      {chosen.length > 1 && (
        <label className="case-toggle">
          <input type="checkbox" checked={combine} onChange={(e) => setCombine(e.target.checked)} />
          also count combinations
        </label>
      )}

      <div className="panel-actions">
        <button className="btn primary" onClick={() => onRun(chosen)} disabled={chosen.length === 0 || unchanged}>
          {scanning ? 'Counting…' : active ? 'Update' : 'Get insights'}
        </button>
        {active && (
          <button className="btn subtle" onClick={onExit}>
            Back to rows
          </button>
        )}
      </div>
      {chosen.length === 0 && <p className="footnote">Pick at least one column.</p>}
    </section>
  )
}
